// What the company sells on the site, by kind and content type — under the master switch
// (ProductsVisibility). Folded to a line that says how it stands ("everything is on sale", or how much
// is paused) with "Manage", which opens the matrix in two tabs: by delivery type — its physical and
// its digital products, a checkbox each — and by product type — its content types in a table (search,
// a kind and a status filter, pages); each on or paused (vendor {products_visibility: {pause}}). A paused part leaves the site as the whole catalog
// does with the master switch, and returns when turned back on.
//
// A cell is the vendor's own pause, or Aldoo's hold (a platform admin's pause, with a reason for the
// vendor if one is given), which only a platform admin lifts. The physical products are also paused
// while the company has no shipping provider delivering (worked out by the API, shown with a way to
// the shipping providers). Listed: the content types the company has products of, and any paused or
// held one (vendor {sales_matrix}).
import React, { useCallback, useEffect, useMemo, useState } from "react"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import DataTable from "aldoo-ra/DataTable"
import Input from "aldoo-ra/Input"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Typography from "aldoo-ra/Typography"
import Tabs from "aldoo-ra/Tabs"
import SectionPicker from "./SectionPicker"
import { showNotification } from "aldoo-ra/Notification"
import { LockClosedIcon, TruckIcon, AdjustmentsHorizontalIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { GetSalesMatrix, SetSalesPause } from "../AdminShared/api"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"
import { Field, TextArea, Chip, Select, Toggle, errorMessage } from "../AdminShared/ui"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import { useUrlParam } from "aldoo-ra/URLState"
import TypeProductsDialog, { ProductsAvailability } from "./TypeProductsDialog"

// the dialog open, and its tab, in the address (sales=delivery|product): a link or a reload opens it where it was
const SALES_TABS = ["delivery", "product", "products"]

const KIND_LABELS = { physical: "Physical products", digital: "Digital products" }
const STATUS_LABELS = { selling: "Selling", paused: "Paused by you", held: "On hold by Aldoo", auto: "Paused: no shipping provider", kind: "Paused by its delivery type", catalog: "Paused with the whole catalog" }
const STATUS_CLASSES = {
  selling: "bg-admin_success/10 text-admin_success",
  catalog: "bg-amber-100 text-amber-900",
  paused: "bg-admin_neutral_1 text-admin_text_secondary",
  held: "bg-amber-100 text-amber-900",
  auto: "bg-amber-100 text-amber-900",
  kind: "bg-admin_neutral_1 text-admin_text_secondary",
}

/** Aldoo's reason for holding a part of the catalog, asked when a platform admin pauses it. */
function PartHoldDialog({ name, onConfirm, onClose, busy }) {
  const t = usePortalTexts(COMPANY)
  const [reason, setReason] = useState("")
  return (
    <Modal isOpen onClose={onClose} title={t("Put on hold: %s", name)} className="max-w-[560px]">
      <div className="flex flex-col gap-4 p-1" data-testid="sales-hold-dialog">
        <div className="text-sm text-admin_text_secondary">{t`These products leave the site until Aldoo brings them back. The vendor cannot turn them on again.`}</div>
        <Field label={t`Reason shown to the vendor`} hint={t`Optional. Without one, the vendor only sees that the catalog is on hold.`}>
          <TextArea value={reason} onChange={setReason} rows={4} maxLength={2000} data-testid="sales-hold-reason" />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>
            {t`Cancel`}
          </Button>
          <Button variant="admin_primary" onClick={() => onConfirm(reason)} disabled={busy} data-testid="sales-hold-confirm">
            {t`Put on hold`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/** The way to the shipping providers (Delivery → Shipping providers) from the vendor portal. */
const providersHref = () => {
  if (typeof window === "undefined") return "#"
  const url = new URL(window.location.href)
  url.searchParams.set("section", "shipping")
  url.searchParams.set("delivery", "providers")
  // the dialog stays behind: back on the settings it is not open again
  url.searchParams.delete("sales")
  url.searchParams.delete("settings")
  return `${url.pathname}${url.search}`
}

/** How a cell stands: its own pause (Aldoo's hold or the vendor's), else what turns it off from above — the whole catalog off before anything. */
const statusOf = ({ pause, auto = false, offWithKind = false, catalogOff = false }) => (pause?.by === "admin" ? "held" : pause ? "paused" : auto ? "auto" : offWithKind ? "kind" : catalogOff ? "catalog" : "selling")

function StatusChip({ status }) {
  const t = usePortalTexts(COMPANY)
  return (
    <Chip className={STATUS_CLASSES[status]}>
      <span className="flex items-center gap-1 whitespace-nowrap">
        {status === "held" && <LockClosedIcon className="w-3.5 h-3.5" />}
        {t(STATUS_LABELS[status])}
      </span>
    </Chip>
  )
}

/** The matrix: the kinds as checkboxes, the content types in a table. */
function ManageDialog({ matrix, canEdit, catalogOff, isPlatformAdmin, adminPage, busy, onToggle, onClose, version, tab, setTab }) {
  const t = usePortalTexts(COMPANY)
  const [kindFilter, setKindFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  // the search, first on the left with the filters (the table's own sits over them on the right)
  const [term, setTerm] = useState("")
  // the type whose products are being looked at (TypeProductsDialog)
  const [browsing, setBrowsing] = useState(null)
  const kindOf = (kind) => matrix.kinds.find((entry) => entry.kind === kind) || { kind }
  const kindOff = (kind) => !!kindOf(kind).pause || !!kindOf(kind).auto
  // both delivery types, always: a kind it does not sell yet can be paused ahead of its first product
  const kinds = ["digital", "physical"]
  const rows = useMemo(() => matrix.types.map((type) => ({ ...type, _id: type.contentId, status: statusOf({ pause: type.pause, offWithKind: kindOff(type.kind), catalogOff }) })), [matrix, catalogOff])
  // the "All products" tab: each row names its type; read the first time the tab opens, then kept
  const typeNames = useMemo(() => Object.fromEntries(matrix.types.map((type) => [type.contentId, type.name])), [matrix])
  // what keeps a type's products off the store whatever their own checkbox says, first the widest:
  // the whole catalog, its delivery type (Aldoo's hold, the company's pause, no shipping provider),
  // then the type itself (Aldoo's hold, the company's pause); null when nothing does
  const overrideOf = (contentType) => {
    if (catalogOff) return "catalog"
    const type = matrix.types.find((entry) => entry.contentId === contentType)
    if (!type) return null
    const kind = kindOf(type.kind)
    if (kind.pause) return kind.pause.by === "admin" ? "held" : "kind"
    if (kind.auto) return "auto"
    if (type.pause) return type.pause.by === "admin" ? "held" : "type"
    return null
  }
  const [allSeen, setAllSeen] = useState(tab === "products")
  useEffect(() => {
    if (tab === "products") setAllSeen(true)
  }, [tab])
  // `saving: false`: what locks it for good, without the moment a change is saving
  const locked = (pause, { saving = true } = {}) => !canEdit || catalogOff || (saving && busy) || (pause?.by === "admin" && !isPlatformAdmin)
  // a product type under a paused delivery type: its own switch stays as it was, but the delivery type decides

  // the table reads its page from the matrix in hand: searched, filtered, a page of it
  const apiMethod = async ({ page = 1, itemsPerPage = 10 }) => {
    const needle = term.trim().toLowerCase()
    const found = rows.filter(
      (row) =>
        (!kindFilter || row.kind === kindFilter) &&
        (!statusFilter || row.status === statusFilter) &&
        (!needle || String(row.name).toLowerCase().includes(needle) || String(row.contentId).toLowerCase().includes(needle))
    )
    return { data: found.slice((page - 1) * itemsPerPage, page * itemsPerPage), total: found.length }
  }

  const columns = [
    { id: "name", title: t`Type`, render: (value, row) => <span className="font-semibold text-admin_text">{value}</span> },
    { id: "kind", title: t`Delivery type`, headerClassName: "w-[180px]", render: (value) => <span className="text-sm text-admin_text_secondary">{t(KIND_LABELS[value])}</span> },
    {
      id: "products",
      title: t`Products`,
      headerClassName: "w-[110px]",
      // the count opens the products themselves: what this type's switch takes off the store or brings back
      render: (value, row) =>
        value > 0 ? (
          <button type="button" onClick={() => setBrowsing(row)} className="text-sm font-semibold text-admin_primary hover:underline underline-offset-2" data-testid={`sales-type-products-${row.contentId}`}>
            {value}
          </button>
        ) : (
          <span className="text-sm">{value}</span>
        ),
    },
    {
      id: "status",
      title: t`Status`,
      headerClassName: "w-[260px]",
      render: (value, row) => (
        <div className="flex flex-col gap-1">
          <StatusChip status={value} />
          {value === "held" && row.pause?.reason && <span className="text-xs text-amber-900 whitespace-pre-line">{row.pause.reason}</span>}
        </div>
      ),
    },
    {
      id: "actions",
      title: "",
      headerClassName: "w-[80px]",
      // a switch they cannot turn is not shown (its delivery type decides, Aldoo holds it, no rights);
      // while a change saves it stays, dimmed
      render: (_, row) =>
        kindOff(row.kind) || locked(row.pause, { saving: false }) ? null : (
          <span className={busy ? "opacity-50 pointer-events-none" : ""} data-testid={`sales-type-${row.contentId}`}>
            <ToggleSwitch variant="admin" isOn={!row.pause} onChange={(on) => onToggle({ scope: "type", value: row.contentId, name: row.name }, !on)} />
          </span>
        ),
    },
  ]

  const salesTabs = [
    { value: "delivery", label: t`By delivery type` },
    { value: "product", label: t`By product type` },
    { value: "products", label: t`All products` },
  ]

  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="admin"
      closeOnEscape
      styleOverrides={{ container: "w-[min(96vw,1040px)] max-h-full md:max-h-[calc(100vh-2rem)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}
    >
      <div className="flex flex-col gap-4 p-5 pt-1" data-testid="sales-manage-dialog">
        {browsing && <TypeProductsDialog orgId={matrix.orgId} contentType={browsing.contentId} name={browsing.name} overrideOf={overrideOf} onClose={() => setBrowsing(null)} />}
        <div>
          <div className="text-lg font-semibold text-admin_text">{t`Store availability`}</div>
          <div className="text-xs text-admin_text_secondary">{t`Choose which kinds and types of your products are available in the store. A paused one leaves the store and comes back when you turn it on again.`}</div>
          {catalogOff && <div className="text-xs text-amber-800 pt-1">{t`The whole catalog is off the site: the "Show my products on the site" switch in Settings is off.`}</div>}
        </div>

        {/* below lg a dropdown (its list under it: a sheet over this dialog would be one layer too many), from lg the tabs */}
        <SectionPicker tabs={salesTabs} value={tab} onChange={setTab} sheet={false} testId="sales-tabs-select" />
        <Tabs variant="admin" value={tab} onChange={setTab} tabs={salesTabs} className="hidden lg:block" data-testid="sales-tabs" />

        {/* both tabs in one cell of a grid, the other one kept but invisible: the dialog is as tall as
            the taller of them and does not jump when the tab changes */}
        {/* the column no wider than the dialog: else the widest tab (the cards' long names) pushes past a phone's screen */}
        <div className="grid grid-cols-[minmax(0,1fr)]">
        {/* by delivery type: the kinds, a checkbox each, on while it sells; it comes before the product types */}
        <div className={`col-start-1 row-start-1 min-w-0 flex flex-col gap-2 ${tab === "delivery" ? "" : "invisible pointer-events-none"}`} aria-hidden={tab !== "delivery"}>
          <p className="m-0 rounded-md border border-admin_bordered bg-admin_neutral_1/40 px-3 py-2 text-xs text-admin_text_secondary" data-testid="sales-kind-note">
            {t`Pausing a delivery type pauses all of its product types, whatever their own setting: the delivery type takes precedence.`}
          </p>
          <div className="rounded-lg border border-admin_bordered divide-y divide-admin_bordered" data-testid="sales-kinds">
            {kinds.map((kind) => {
              const entry = kindOf(kind)
              const status = statusOf({ ...entry, catalogOff })
              const name = t(KIND_LABELS[kind])
              const products = matrix.types.filter((type) => type.kind === kind).reduce((sum, type) => sum + (type.products || 0), 0)
              return (
                <div key={kind} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3" data-testid={`sales-kind-${kind}`}>
                  <span className="flex-1 min-w-[200px]">
                    {/* keyed by the matrix read: a hold the admin cancels draws the checkbox again as saved
                        (the checkbox keeps its own state until its value changes) */}
                    {/* no courier yet: the physical products cannot be sold, so not shown as on, nor turned on —
                        a pause already there can still be lifted */}
                    <Toggle key={`${kind}-${version}`} checked={!entry.pause && !entry.auto} onChange={(on) => onToggle({ scope: "kind", value: kind, name }, !on)} label={name} disabled={locked(entry.pause) || (!products && !entry.pause) || (entry.auto && !entry.pause)} className="font-semibold" />
                  </span>
                  {/* a delivery type it does not sell: said so, rather than "0 products, selling", and its checkbox off
                      limits — a pause already on it still shows, and can be lifted */}
                  {products > 0 ? (
                    <span className="text-sm text-admin_text_secondary">{t("%s products", products)}</span>
                  ) : (
                    <span className="text-sm text-admin_text_secondary" data-testid={`sales-kind-empty-${kind}`}>{t`You don't have products of this type in your store`}</span>
                  )}
                  {(products > 0 || entry.pause) && <StatusChip status={status} />}
                  {status === "held" && entry.pause?.reason && <span className="basis-full text-xs text-amber-900 whitespace-pre-line">{entry.pause.reason}</span>}
                  {/* no courier: said whether or not there are physical products yet, with the way to add one */}
                  {entry.auto && (
                    <div className="basis-full flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sales-no-provider">
                      <span>{t`No shipping provider yet: physical products cannot be sold until you add one.`}</span>
                      {!adminPage && (
                        <a href={providersHref()} className="font-semibold text-admin_primary underline underline-offset-2 inline-flex items-center gap-1" data-testid="sales-add-provider">
                          <TruckIcon className="w-3.5 h-3.5" />
                          {t`Add a shipping provider`}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className={`col-start-1 row-start-1 min-w-0 flex flex-col gap-4 ${tab === "product" ? "" : "invisible pointer-events-none"}`} aria-hidden={tab !== "product"}>
        {/* a paused delivery type decides for its product types: said once, above the table (each row's switch says so on hover) */}
        {kinds.some((kind) => kindOff(kind)) && (
          <p className="m-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sales-types-controlled">
            {t("%s: paused by the delivery type setting, which controls all of their product types.", kinds.filter((kind) => kindOff(kind)).map((kind) => t(KIND_LABELS[kind])).join(", "))}
          </p>
        )}
        {/* search, then the filters — on the left, above the table */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full sm:w-[280px]" data-testid="sales-search">
            <Input
              variant="admin"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t`Search types…`}
              className="w-full"
              icon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />}
            />
          </div>
          <Select value={kindFilter} onChange={setKindFilter} placeholder={t`Any delivery type`} options={Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label: t(label) }))} className="w-[190px]" data-testid="sales-filter-kind" />
          <Select value={statusFilter} onChange={setStatusFilter} placeholder={t`Any status`} options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label: t(label) }))} className="w-[230px]" data-testid="sales-filter-status" />
        </div>

        {/* every switch saves as it is turned: nothing to confirm, the dialog just closes */}
        {/* the table's frame from md; a phone's cards have their own borders */}
        <div className="md:border md:border-admin_bordered md:rounded-lg">
          <DataTable
            variant="admin_browser"
            preferredHeight="60vh"
            apiMethod={apiMethod}
            showSearch={false}
            renders={columns}
            refreshKey={JSON.stringify([kindFilter, statusFilter, term, version])}
            itemsPerPage={10}
            mobileCards
            paginationOfLabel={t`of`}
            styleOverrides={{ container: "md:h-auto" }}
            noData={<Typography variant="admin_body1">{t`No content types`}</Typography>}
          />
        </div>
        </div>
        {/* every product of the company, one by one: the same list a type's products open in, its
            type named on each row; read once the tab is first opened */}
        <div className={`col-start-1 row-start-1 min-w-0 flex flex-col gap-4 ${tab === "products" ? "" : "invisible pointer-events-none"}`} aria-hidden={tab !== "products"} data-testid="sales-all-products">
          {(tab === "products" || allSeen) && <ProductsAvailability orgId={matrix.orgId} typeNames={typeNames} overrideOf={overrideOf} />}
        </div>
        </div>
      </div>
    </Modal>
  )
}

export default function SalesMatrix({ org, canEdit, isPlatformAdmin = false, adminPage = false, catalogOff = false }) {
  const t = usePortalTexts(COMPANY)
  const language = useSiteLanguage()
  const [matrix, setMatrix] = useState(null)
  const [busy, setBusy] = useState(false)
  // by delivery type (the kinds) or by product type (the content types); none: the dialog closed
  const [salesTab, setSalesTab] = useUrlParam("sales")
  const managing = SALES_TABS.includes(salesTab)
  // the matrix read again: the table's pages follow
  const [version, setVersion] = useState(0)
  // the cell a platform admin is about to hold: { scope, value, name }
  const [asking, setAsking] = useState(null)

  const load = useCallback(async () => {
    const res = await GetSalesMatrix(org._id, language)
    if (!res?.matrix) return
    setMatrix(res.matrix)
    setVersion((n) => n + 1)
  }, [org._id, language])
  // again when the company's couriers or switches change (the org saved elsewhere on the page)
  useEffect(() => {
    load()
  }, [load, org.shippingProviders, org.salesPaused, org.productsHidden])

  const save = async ({ scope, value, name }, paused, reason) => {
    setBusy(true)
    try {
      const res = await SetSalesPause(org._id, scope, value, paused, reason)
      const error = errorMessage(res, t`Request failed`)
      showNotification({
        // the same words as a product's checkbox in its type's list (TypeProductsDialog)
        message: error || (paused ? t("Off the store: %s", name) : t("Available in store: %s", name)),
        className: error ? "bg-admin_error text-white" : "bg-admin_success text-white",
      })
      if (error) return
      setAsking(null)
      await load()
    } finally {
      setBusy(false)
    }
  }
  // `pause`: true takes that part off the site; a platform admin's pause is a hold, and may say why
  const toggle = (cell, pause) => {
    if (busy) return
    if (pause && isPlatformAdmin) return setAsking(cell)
    save(cell, pause)
  }

  if (!matrix) return null
  const kindOf = (kind) => matrix.kinds.find((entry) => entry.kind === kind) || { kind }
  // how it stands, in a line: what is off of what it sells
  const kindsOff = ["physical", "digital"].filter((kind) => matrix.types.some((type) => type.kind === kind) && (kindOf(kind).pause || kindOf(kind).auto))
  const typesOff = matrix.types.filter((type) => type.pause && !kindsOff.includes(type.kind))
  const held = [...matrix.kinds, ...matrix.types].some((cell) => cell.pause?.by === "admin")
  const off = kindsOff.length + typesOff.length

  return (
    <div className="flex flex-col items-start gap-2 pt-2" data-testid="sales-matrix">
      <div>
        <div className="text-sm font-semibold text-admin_text inline-flex items-center gap-1.5">{t`Store availability`}<HelpHint helpKey="vendor.settings#availability" /></div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-admin_text_secondary" data-testid="sales-summary">
          <span>
            {!matrix.types.length
              ? t`You have no products yet.`
              : !off
                ? t`Everything is on sale`
                : [...kindsOff.map((kind) => t(KIND_LABELS[kind])), ...typesOff.map((type) => type.name)].join(", ") + " — " + t("%s paused", off)}
          </span>
          {held && <StatusChip status="held" />}
          {kindsOff.includes("physical") && kindOf("physical").auto && <StatusChip status="auto" />}
        </div>
      </div>
      {matrix.types.length > 0 && (
        <Button variant="admin_secondary" onClick={() => setSalesTab("delivery", { history: "push" })} data-testid="sales-manage">
          <span className="flex items-center gap-1.5">
            <AdjustmentsHorizontalIcon className="w-4 h-4" />
            {t`Manage`}
          </span>
        </Button>
      )}
      {managing && (
        <ManageDialog
          matrix={matrix}
          version={version}
          canEdit={canEdit}
          catalogOff={catalogOff}
          isPlatformAdmin={isPlatformAdmin}
          adminPage={adminPage}
          busy={busy}
          onToggle={toggle}
          tab={salesTab}
          setTab={setSalesTab}
          onClose={() => setSalesTab(null)}
        />
      )}
      {asking && (
        <PartHoldDialog
          name={asking.name}
          busy={busy}
          onClose={() => {
            setAsking(null)
            // nothing saved: the switches go back to what is stored
            setVersion((n) => n + 1)
          }}
          onConfirm={(reason) => save(asking, true, reason)}
        />
      )}
    </div>
  )
}
